import {
	Alert,
	Box,
	Button,
	DefinitionList,
	DefinitionListItem,
	DefinitionListItemData,
	DefinitionListItemHeading,
	designTokens,
	Flex,
	Grid,
	Input,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import type React from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { useDealContext } from "~/context/DealContext";
import { useVendorSessionContext } from "~/context/VendorSession";
import { REVERT_STATUS_TEXT } from "~/models/Status";
import { VENDOR_COMPANY_ITANDI_ID } from "~/models/VendorCompany";
import { update as updateAwaitingAcceptanceStatus } from "~/utils/api/admin/deals/RevertStatus";
import { isSuccess } from "~/utils/api/api_handler";
import { formatHyphenDate } from "~/utils/DateUtil";
import { generatePath } from "~/utils/generatePath";
import Styles from "./statuses.module.scss";

export const Finished: React.FC = () => {
	const deal = useDealContext();
	const session = useVendorSessionContext();
	const { mutate: globalMutate } = useSWRConfig();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [confirmText, setConfirmText] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const revertStatus = useCallback(async () => {
		setIsLoading(true);
		const result = await updateAwaitingAcceptanceStatus(deal.id);
		if (isSuccess(result)) {
			toast.success("ステータスを戻しました");
			await globalMutate(generatePath("/vendors/deals/:id", { id: deal.id }));
			setIsModalOpen(false);
		} else {
			toast.error(`エラーが発生しました。${result.message}`);
		}
		setIsLoading(false);
	}, [deal.id, globalMutate]);

	return (
		<div>
			<h3 className={Styles.Title}>完了しました</h3>
			<Box marginY={16}>
				<p>すべての工事工程が完了しました。</p>
			</Box>
			{session.admin &&
			session.vendorCompany.id === VENDOR_COMPANY_ITANDI_ID ? (
				<Flex justifyContent="end">
					<Button
						onClick={() => {
							setIsModalOpen(true);
						}}
						variant="danger"
					>
						ステータスを戻す
					</Button>
				</Flex>
			) : null}
			{isModalOpen ? (
				<Modalv2
					isOpen
					onClose={() => {
						setIsModalOpen(false);
					}}
				>
					<ModalHeader title="完了した案件のステータスを戻す" />
					<ModalBody>
						<Grid gap={designTokens["spacing/m"]}>
							<p>以下の案件のステータスを完了から検収中に戻します。</p>
							<DefinitionList>
								<DefinitionListItem>
									<DefinitionListItemHeading>案件ID</DefinitionListItemHeading>
									<DefinitionListItemData>{deal.id}</DefinitionListItemData>
								</DefinitionListItem>
								<DefinitionListItem>
									<DefinitionListItemHeading>物件名</DefinitionListItemHeading>
									<DefinitionListItemData>{deal.name}</DefinitionListItemData>
								</DefinitionListItem>
								<DefinitionListItem>
									<DefinitionListItemHeading>
										部屋番号
									</DefinitionListItemHeading>
									<DefinitionListItemData>
										{deal.roomNumber}
									</DefinitionListItemData>
								</DefinitionListItem>
								<DefinitionListItem>
									<DefinitionListItemHeading>完了日</DefinitionListItemHeading>
									<DefinitionListItemData>
										{formatHyphenDate(deal.completedDate)}
									</DefinitionListItemData>
								</DefinitionListItem>
							</DefinitionList>
							<Alert variant="warning">
								マネージャーの承認なしにステータスを完了から戻すことはできません。
								<br />
								必ず承認を得た上で、表示されている案件が間違いないかを確認し、
								<br />
								以下の欄に
								{`「${REVERT_STATUS_TEXT}」と入力して進めてください。`}
							</Alert>
							<Input
								onChange={(e) => {
									setConfirmText(e.target.value);
								}}
								placeholder={REVERT_STATUS_TEXT}
								value={confirmText}
							/>
						</Grid>
					</ModalBody>
					<ModalFooter
						leftArea={
							<Button
								onClick={() => {
									setIsModalOpen(false);
								}}
								variant="secondary"
							>
								閉じる
							</Button>
						}
						rightArea={
							<Button
								disabled={confirmText !== REVERT_STATUS_TEXT}
								loading={isLoading}
								onClick={revertStatus}
								variant="danger"
							>
								ステータスを戻す
							</Button>
						}
					/>
				</Modalv2>
			) : null}
		</div>
	);
};
